import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Calendar, MapPin, Clock, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { showroomFallback } from '../data';

export default function Showroom() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/showroom_page')
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setContent(data[0]);
        } else {
          setContent(showroomFallback);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setContent(showroomFallback);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null || !content?.gallery) return;
      if (e.key === 'Escape') {
        setSelectedImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        setSelectedImageIndex((prev) => (prev === 0 ? content.gallery.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setSelectedImageIndex((prev) => (prev === content.gallery.length - 1 ? 0 : prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, content?.gallery]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-none border-4 border-[#133b54] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-white py-20 sm:py-24 lg:py-28 min-h-[350px] flex items-center">
        <img
          src={content?.hero_image[0] || "https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/f299cec7-df78-4423-ab45-676f8e2adf52.jpeg"}
          alt=""
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white via-white/80 to-transparent" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-left">
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-4xl font-bold tracking-tight text-[#1e335e] sm:text-7xl uppercase"
            >
              {content?.title.da || "Besøg vores Showroom"}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-xl leading-8 text-gray-900 font-medium"
            >
              Oplev vores produkter i virkeligheden og få professionel rådgivning til din næste profilering.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-16">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-[#1e335e] sm:text-4xl uppercase">Hvorfor besøge os?</h2>
            <div 
              className="mt-6 text-lg leading-8 text-gray-600 prose prose-blue"
              dangerouslySetInnerHTML={{ __html: content?.content.da || "" }}
            />
            
            <dl className="mt-10 space-y-2 text-base leading-7 text-gray-600">
              {[
                'Se og mærk kvaliteten af vores tøj',
                'Prøv forskellige størrelser og pasformer',
                'Få inspiration til firmagaver',
                'Personlig rådgivning fra vores eksperter'
              ].map((feature) => (
                <div key={feature} className="relative pl-9">
                  <dt className="inline font-semibold text-gray-900">
                    <Check className="absolute left-1 top-1 h-5 w-5 text-black" aria-hidden="true" />
                  </dt>
                  <dd className="inline">{feature}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="bg-[#133b54] p-8 rounded-none border border-white/10">
            <h3 className="text-xl font-bold text-white mb-8 uppercase">Book et møde</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Adresse</p>
                  <p className="text-white/80">Meterbuen 33<br />2740 Skovlunde</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Åbningstider</p>
                  <p className="text-orange-400 font-bold">Kun efter aftale (Vigtigt!)</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Besøg i showroom</p>
                  <p className="text-white/80">Vi kan kun tage imod besøgende efter forudgående aftale.</p>
                </div>
              </div>
            </div>

            <Link 
              to="/kontakt"
              className="mt-10 block w-full text-center rounded-none bg-orange-500 px-8 py-4 text-sm font-semibold text-white hover:bg-orange-600 transition-all transform hover:-translate-y-1 hover:shadow-lg duration-200 uppercase"
            >
              Book tid i showroomet
            </Link>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      {content?.gallery && content.gallery.length > 0 && (
        <div className="bg-slate-50 py-16 sm:py-24 border-t border-slate-100">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-12 sm:mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-[#1e335e] sm:text-4xl uppercase">
                Billeder fra showroomet
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Gå på opdagelse i vores store udstilling og få et indtryk af det brede udvalg, vi tilbyder.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {content.gallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative aspect-4/3 overflow-hidden bg-gray-100 cursor-pointer"
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={image}
                    alt={`Udstilling ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Maximize2 className="h-8 w-8 text-white transform scale-75 group-hover:scale-100 transition-transform duration-300" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedImageIndex !== null && content?.gallery && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 md:p-10"
          onClick={() => setSelectedImageIndex(null)}
        >
          <button
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-4 right-4 text-white hover:text-orange-500 transition-colors p-2 z-50"
            aria-label="Luk"
          >
            <X className="h-8 w-8" />
          </button>

          {content.gallery.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex((prev) => (prev === 0 ? content.gallery.length - 1 : prev - 1));
                }}
                className="absolute left-4 text-white hover:text-orange-500 transition-colors p-2 z-50"
                aria-label="Forrige"
              >
                <ChevronLeft className="h-10 w-10" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex((prev) => (prev === content.gallery.length - 1 ? 0 : prev + 1));
                }}
                className="absolute right-4 text-white hover:text-orange-500 transition-colors p-2 z-50"
                aria-label="Næste"
              >
                <ChevronRight className="h-10 w-10" />
              </button>
            </>
          )}

          <div 
            className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.img
              key={selectedImageIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              src={content.gallery[selectedImageIndex]}
              alt={`Udstilling ${selectedImageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="mt-4 text-white/80 text-sm font-medium">
              Billede {selectedImageIndex + 1} af {content.gallery.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
